import { moneyToNumber, roundTo2, toMoney } from './decimal.js';
import { addMonths, periodRate, graceMonths } from './day-count.js';
import type { AmortizationInput, AmortizationResult, AmortizationRow } from './types.js';

/**
 * French method (cuota fija): constant total installment, decreasing
 * interest, increasing principal.
 *
 * `PMT = P * r / (1 - (1+r)^-n)` where r is the periodic rate.
 *
 * For mixed day-count schedules where r varies per period, we still use the
 * first period's rate to size PMT (industry convention for monthly schedules
 * where rate variance is small). The last installment absorbs the rounding
 * remainder so the schedule closes exactly to zero.
 */
export function amortizeFrench(input: AmortizationInput): AmortizationResult {
  const { principal, annualRate, termMonths, startDate, dayCount, roundingMode } = input;

  const P = moneyToNumber(principal);
  const r = Number(annualRate);
  if (!Number.isFinite(r) || r < 0) throw new Error(`invalid annual rate: ${annualRate}`);
  if (termMonths <= 0) throw new Error(`term must be positive: ${termMonths}`);

  let prevDate = startDate;
  const dueDates: string[] = [];
  for (let i = 1; i <= termMonths; i++) {
    dueDates.push(addMonths(startDate, i));
  }

  // Interest grace: installments 1..(interestStartMonth-1) accrue no interest.
  // `g` = interest-free months up front (clamped so at least one month bears interest).
  const g = graceMonths(input.interestStartMonth, termMonths);
  // Model A — one constant cuota for the whole term. During grace the whole
  // payment is capital; C = P / (g + a) with `a` the annuity factor over the
  // `m` interest-bearing months, sized from the first interest-bearing period's
  // rate (mirrors the "first period's rate sizes PMT" convention). At g=0 this
  // reduces exactly to P*sr/(1-(1+sr)^-n), or P/n when sr=0.
  const sizingPrev = g === 0 ? startDate : dueDates[g - 1]!;
  const sr = periodRate(dayCount, r, sizingPrev, dueDates[g]!);
  const m = termMonths - g;
  const annuity = sr === 0 ? m : (1 - Math.pow(1 + sr, -m)) / sr;
  const pmtRaw = P / (g + annuity);

  const rows: AmortizationRow[] = [];
  let balance = P;

  for (let i = 0; i < termMonths; i++) {
    const dueDate = dueDates[i]!;
    const pr = i < g ? 0 : periodRate(dayCount, r, prevDate, dueDate);
    const interest = roundTo2(balance * pr, roundingMode);

    const isLast = i === termMonths - 1;
    let principalAmt: number;
    let totalAmt: number;

    if (isLast) {
      principalAmt = roundTo2(balance, roundingMode);
      totalAmt = roundTo2(principalAmt + interest, roundingMode);
    } else {
      const pmt = roundTo2(pmtRaw, roundingMode);
      principalAmt = roundTo2(pmt - interest, roundingMode);
      totalAmt = roundTo2(principalAmt + interest, roundingMode);
    }

    const opening = roundTo2(balance, roundingMode);
    const closing = roundTo2(balance - principalAmt, roundingMode);

    rows.push({
      installmentNumber: i + 1,
      dueDate,
      openingBalance: opening.toFixed(2),
      principalAmount: principalAmt.toFixed(2),
      interestAmount: interest.toFixed(2),
      totalAmount: totalAmt.toFixed(2),
      closingBalance: closing.toFixed(2),
    });

    balance = balance - principalAmt;
    prevDate = dueDate;
  }

  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalPaid = 0;
  for (const row of rows) {
    totalPrincipal += moneyToNumber(row.principalAmount);
    totalInterest += moneyToNumber(row.interestAmount);
    totalPaid += moneyToNumber(row.totalAmount);
  }

  return {
    method: 'french',
    dayCount,
    totalPrincipal: toMoney(totalPrincipal, roundingMode),
    totalInterest: toMoney(totalInterest, roundingMode),
    totalPaid: toMoney(totalPaid, roundingMode),
    rows,
  };
}
